import { useEffect, useId, useState } from 'react'
import { Collapse } from '@mui/material'
import { spacing, useIsMobileVariant } from '../../../theme'
import { BareButton } from '../../../primitives/Button'
import { ExpandMore } from '../../../primitives/ExpandMore'
import { CardInternal, type CardProps } from '../CardInternal'
import { BaseCardHeaderAccordion, type CardHeaderProps } from '../BaseCard.Header'
import { BaseCardContent } from '../BaseCard.Content'
import { BaseCardFooter } from '../BaseCard.Footer'

export type CardAccordionProps = {
  /**
   * Keep it short, or the layout will shorten it for you :)
   */
  title: string

  /**
   * To support your title or give glance what information can be expected like name Shipper etc
   */
  summary?: string | null

  /**
   * You can initially have the component open or not
   * @default false
   */
  isOpen?: boolean

  /**
   * Not keeping it mounted after closing
   * @default true
   */
  unmountOnExit?: boolean

  /**
   * You can integrate the Accordion with a statemachine which stores its state for example
   * Only passed when user interacts, not on its initial `isOpen` value
   */
  onOpened?: () => void

  /**
   * You can integrate the Accordion with a statemachine which stores its state for example
   * Only passed when user interacts, not on its initial `isOpen` value
   */
  onClosed?: () => void

  /**
   * Sometimes you just want to know if an item is clicked or not, not if its opened or not (`onOpened` and `onClosed`)
   */
  onClick?: () => void
} & Omit<CardProps, 'dividers'> &
  Pick<CardHeaderProps, 'component'>

/**
 * The <CardAccordion> component is a flexible container used to group content and actions about a single subject.
 * It can hold text, media, toolbars, and buttons, and adapts to both mobile and desktop layouts. You can start
 * it initally collapsed or not.
 *
 *  Demos:
 * - [CardAccordion](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/constructs-card-cardaccordion--docs)
 *
 */
const CardAccordion = ({
  children,
  title,
  summary,
  isOpen = false,
  unmountOnExit = true,
  component,
  onOpened,
  onClosed,
  onClick,
  ...props
}: CardAccordionProps) => {
  const id = useId()
  const [open, setIsOpen] = useState(isOpen)
  const isMobile = useIsMobileVariant()
  const labelControl = `${id}-content`
  const labelHeader = `${id}-header`

  const handleToggle = () => {
    const state = !open

    if (state) {
      onOpened?.()
    } else {
      onClosed?.()
    }

    onClick?.()

    setIsOpen((prevState) => !prevState)
  }

  // reason: readable still using spacing and not number since MUI sx is not transforming these :)
  // eslint-disable-next-line no-nested-ternary
  const radius = isMobile ? 0 : open ? 0 : spacing(0.25)

  /**
   * Now its able to be controlled from the outside to suddenly open or close remotely without user interactions on the Header
   */
  useEffect(() => {
    setIsOpen(isOpen)
  }, [isOpen])

  return (
    <CardInternal {...props}>
      <BareButton
        onClick={handleToggle}
        data-testid="card-accordion-header"
        aria-expanded={open}
        aria-controls={labelControl}
        id={labelHeader}
        sx={({ misc }) => ({
          width: '100%',
          alignItems: 'flex-start',
          justifyContent: 'start',
          borderRadius: isMobile ? 0 : misc.borderRadius(2),
          borderEndEndRadius: radius,
          borderEndStartRadius: radius,
          pb: open ? 0 : 2,

          ...misc.createHoverOrActiveState({
            backgroundColor: 'action.hover',
          }),

          ...misc.createFocusVisibleState({
            backgroundColor: 'action.focus',
            outline: misc.borderStyle('pure.dark', 2),
            outlineOffset: misc.borderSize(-2),
          }),
        })}
      >
        <BaseCardHeaderAccordion
          title={title}
          component={component}
          endNode={<ExpandMore isOpen={open} />}
          summary={summary}
          isOpen={open}
        />
      </BareButton>

      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit={unmountOnExit}
        id={labelControl}
        aria-labelledby={labelHeader}
        role="region"
      >
        {children}
      </Collapse>
    </CardInternal>
  )
}

CardAccordion.Content = BaseCardContent
CardAccordion.Footer = BaseCardFooter

export { CardAccordion }
